"use strict";
/**
 * Projects-public canonical definition — the single source of truth for the
 * `projects-public` OpenSearch index family (owned/registered by the setup lambda).
 *
 * Per the canonical-shared-types model, `shared-types` (the pure, AWS-SDK-free contract
 * package) holds, per index family, BOTH:
 *   (a) the field-name constants (`PROJECT_FIELDS`) — runtime values typed against
 *       `ProjectSearchDocument` keys so the contract and the constants cannot drift, and
 *   (b) the OpenSearch template body (`buildProjectsPublicTemplate`) — settings
 *       (INCLUDING the `analysis` analyzers/normalizers), `dynamic: true`, and explicit
 *       `properties`.
 *
 * Every other surface (the indexer writer, the search reader) references these instead of
 * authoring its own copy, so project field names have exactly one source.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PROJECT_FIELDS = void 0;
exports.buildProjectsPublicTemplate = buildProjectsPublicTemplate;
/**
 * Canonical field-name constants for the `projects-public` index family.
 *
 * Each value is a field name constrained to be a real key of `ProjectSearchDocument` via
 * `satisfies Record<string, keyof ProjectSearchDocument>`, so a typo or a rename that
 * breaks the contract fails to compile. Covers the setup-lambda `projects-public` fields
 * that ARE keys of the contract. The setup lambda also maps some fields that are NOT on
 * the interface (`related_pattern_id`/`related_pattern_title`/`related_pattern_author`/
 * `related_pattern_image`, `description`) — those are mapping-only and are kept as plain
 * string keys in the template `properties`, NOT members here.
 */
exports.PROJECT_FIELDS = {
    project_id: 'project_id',
    identity_id: 'identity_id',
    screen_name: 'screen_name',
    title: 'title',
    status: 'status',
    progress_percentage: 'progress_percentage',
    visibility: 'visibility',
    created_at: 'created_at',
    updated_at: 'updated_at',
};
/**
 * Build the composable OpenSearch index-template body for the `projects-public` family.
 *
 * Ported from the setup lambda's inline `projects-public` IndexDefinition (project_id,
 * identity_id, screen_name, related_pattern_id, related_pattern_title,
 * related_pattern_author, related_pattern_image, title, description, status,
 * progress_percentage, visibility, created_at, updated_at), with the `fpw_text` analyzer
 * applied to the free-text fields (`screen_name`, `related_pattern_title`,
 * `related_pattern_author`, `title`, `description`). The full `settings.analysis` block
 * (the `fpw_text` analyzer, the `english_*` filters, and the `lowercase` normalizer) is
 * copied from `materials-public-definition.ts` so the analyzers travel with the mappings.
 * `mappings.dynamic` is the literal `true` (the BF-018 guard).
 *
 * Property keys that are members of `PROJECT_FIELDS` are keyed by the constants so the
 * mapping types exactly the fields the constants name; the mapping-only `related_pattern_*`
 * and `description` keys (NOT `ProjectSearchDocument` keys) are kept as plain string keys.
 */
function buildProjectsPublicTemplate(opts) {
    return {
        index_patterns: [opts.pattern],
        template: {
            settings: {
                number_of_shards: opts.shards ?? 1,
                number_of_replicas: opts.replicas ?? 0,
                refresh_interval: opts.refresh ?? '5s',
                analysis: {
                    analyzer: {
                        fpw_text: {
                            type: 'custom',
                            tokenizer: 'standard',
                            filter: ['lowercase', 'asciifolding', 'english_possessive_stemmer', 'english_stop', 'english_stemmer'],
                        },
                    },
                    filter: {
                        english_stop: { type: 'stop', stopwords: '_english_' },
                        english_stemmer: { type: 'stemmer', language: 'english' },
                        english_possessive_stemmer: { type: 'stemmer', language: 'possessive_english' },
                    },
                    normalizer: {
                        lowercase: { type: 'custom', char_filter: [], filter: ['lowercase', 'asciifolding'] },
                    },
                },
            },
            mappings: {
                dynamic: true,
                properties: {
                    [exports.PROJECT_FIELDS.project_id]: { type: 'keyword' },
                    [exports.PROJECT_FIELDS.identity_id]: { type: 'keyword' },
                    [exports.PROJECT_FIELDS.screen_name]: { type: 'text', analyzer: 'fpw_text' },
                    related_pattern_id: { type: 'keyword' },
                    related_pattern_title: { type: 'text', analyzer: 'fpw_text' },
                    related_pattern_author: { type: 'text', analyzer: 'fpw_text' },
                    related_pattern_image: { type: 'keyword' },
                    [exports.PROJECT_FIELDS.title]: { type: 'text', analyzer: 'fpw_text' },
                    description: { type: 'text', analyzer: 'fpw_text' },
                    [exports.PROJECT_FIELDS.status]: { type: 'keyword' },
                    [exports.PROJECT_FIELDS.progress_percentage]: { type: 'integer' },
                    [exports.PROJECT_FIELDS.visibility]: { type: 'keyword' },
                    [exports.PROJECT_FIELDS.created_at]: { type: 'date' },
                    [exports.PROJECT_FIELDS.updated_at]: { type: 'date' },
                },
            },
        },
        priority: 100,
        _meta: { managed_by: 'fpw', purpose: 'projects-public' },
    };
}
